import {makeAutoObservable} from "mobx";
import { check } from '../http/userAPI';

export default class UserStore {
    constructor() {
        this._isAuth = false;
        this._user = {};
        this._basket = [];
        this._isRefreshing = false; // флаг для предотвращения множественных запросов
        this._lastRefreshTime = 0; // время последнего обновления
        this._refreshCacheTime = 30 * 1000; // кэш на 30 секунд
        makeAutoObservable(this);
        this._loadBasket();
    }

    _getBasketKey() {
        return this._user && this._user.id ? `basket_${this._user.id}` : 'basket_guest';
    }

    _loadBasket() {
        const key = this._getBasketKey();
        const basket = localStorage.getItem(key);
        this._basket = basket ? JSON.parse(basket) : [];
    }

    setIsAuth(bool) {
        this._isAuth = bool;
    }

    setUser(user) {
        // Перед сменой пользователя пытаемся подмержить гостевую корзину
        let guestBasket = [];
        try {
            const rawGuest = localStorage.getItem('basket_guest');
            if (rawGuest) {
                const parsed = JSON.parse(rawGuest);
                if (Array.isArray(parsed)) {
                    guestBasket = parsed;
                }
            }
        } catch (e) {
            console.warn('Не удалось прочитать гостевую корзину', e);
        }

        this._user = { ...user };
        // Загружаем корзину для нового пользователя
        this._loadBasket();

        // Если у гостя были товары — мержим их в корзину пользователя
        if (guestBasket.length > 0) {
            const merged = [...this._basket];

            guestBasket.forEach(guestItem => {
                const idx = merged.findIndex(item =>
                    item.id === guestItem.id &&
                    item.size === guestItem.size &&
                    item.store_id === guestItem.store_id
                );

                if (idx !== -1) {
                    const existing = merged[idx];
                    const maxQty = existing.maxQuantity ?? guestItem.maxQuantity ?? 999;
                    const newQty = (existing.quantity || 1) + (guestItem.quantity || 1);
                    merged[idx] = {
                        ...existing,
                        quantity: Math.min(newQty, maxQty)
                    };
                } else {
                    merged.push(guestItem);
                }
            });

            this._basket = merged;
            this._saveBasket();

            try {
                localStorage.removeItem('basket_guest');
            } catch (e) {
                console.warn('Не удалось очистить гостевую корзину', e);
            }
        }
    }

    get isAuth() {
        return this._isAuth;
    }

    get user() {
        return this._user;
    }

    get basket() {
        return this._basket;
    }

    get isRefreshing() {
        return this._isRefreshing;
    }

    async refreshUser() {
        // Проверяем, не выполняется ли уже запрос
        if (this._isRefreshing) {
            return;
        }

        // Проверяем кэш - если прошло меньше 30 секунд, не делаем запрос
        const now = Date.now();
        if (now - this._lastRefreshTime < this._refreshCacheTime) {
            return;
        }

        this._isRefreshing = true;
        
        try {
            const data = await check();
            if (data) {
                // Обязательно создаём новый объект пользователя для реактивности MobX
                this.setUser(data);
                this.setIsAuth(true);
                this._lastRefreshTime = now;
            } else {
                // Если токена нет, устанавливаем пользователя как неавторизованного
                this.setUser({});
                this.setIsAuth(false);
                localStorage.removeItem('token');
            }
        } catch (e) {
            // Если ошибка 429, не сбрасываем состояние пользователя
            if (e.response?.status === 429) {
                console.warn('Rate limit exceeded for user refresh, keeping current state');
                return;
            }
            
            this.setUser({});
            this.setIsAuth(false);
            localStorage.removeItem('token');
        } finally {
            this._isRefreshing = false;
        }
    }

    async addToBasket(product, size, quantity, store_id, maxQuantity = 999) {
        try {
            quantity = Math.min(quantity, maxQuantity);
            if (product.type === 'toy') {
                if (!store_id && Array.isArray(product.product_stores) && product.product_stores.length > 0) {
                    store_id = product.product_stores[0].storeId;
                }
            }
            const existingItemIndex = this._basket.findIndex(item => {
                if (item.id !== product.id || item.store_id !== store_id) return false;
                if (!size || typeof size !== 'object') return item.size === size;
                const itemSizeVal = item.selectedSize ?? item.size;
                const sizeVal = size.size ?? size.value;
                if (size.name) return item.variant?.name === size.name && item.store_id === store_id;
                return String(itemSizeVal) === String(sizeVal);
            });

            if (existingItemIndex !== -1) {
                const item = this._basket[existingItemIndex];
                const cap = item.maxQuantity ?? maxQuantity;
                this._basket[existingItemIndex].quantity = Math.min(item.quantity + quantity, cap);
            } else {
                let variant = null;
                if (size && typeof size === 'object') {
                    variant = { ...size };
                    if (size && typeof size === 'object' && size.storeId && !size.storeName && product.stores) {
                        const storeObj = product.stores.find(s => s.id === size.storeId);
                        if (storeObj && typeof storeObj.name === 'string') {
                            size.storeName = storeObj.name;
                        }
                    }
                    if (!variant.storeId && product.product_stores && Array.isArray(product.product_stores) && product.product_stores.length > 0) {
                        variant.storeId = product.product_stores[0].storeId;
                        if (product.stores && Array.isArray(product.stores)) {
                            const storeObj = product.stores.find(s => s.id === variant.storeId);
                            if (storeObj && typeof storeObj.name === 'string') {
                                variant.storeName = storeObj.name;
                            }
                        }
                    }
                    // Для игрушек: если variant содержит name, quantity, barcode и т.д., оставляем как объект
                    // Но убеждаемся, что storeName это строка
                    if (variant && typeof variant.storeName !== 'string' && variant.storeName) {
                        variant.storeName = String(variant.storeName);
                    }
                    // Если variant — объект только с storeId и storeName (без других полей), кладём только строку storeName
                    if (
                        variant &&
                        typeof variant === 'object' &&
                        Object.keys(variant).length === 2 &&
                        'storeId' in variant &&
                        'storeName' in variant &&
                        typeof variant.storeName === 'string' &&
                        !variant.name &&
                        !variant.size &&
                        !variant.color
                    ) {
                        variant = variant.storeName;
                    }
                }

                // ГАРАНТИРОВАННЫЙ ПАТЧ: всегда ищем store_name по store_id
                let store_name = null;
                // 1. Если variant (size) — строка (игрушки), используем её
                if (product.type === 'toy' && size && typeof size === 'string') {
                    store_name = size;
                }
                // 2. Если variant (size) — объект с storeName, используем его
                else if (size && typeof size === 'object' && size.storeName) {
                    store_name = size.storeName;
                }
                // 3. Если есть store_id и product.stores, ищем по ним
                if (!store_name && store_id && product.stores && Array.isArray(product.stores)) {
                    const storeObj = product.stores.find(s => String(s.id) === String(store_id));
                    if (storeObj && typeof storeObj.name === 'string') {
                        store_name = storeObj.name;
                    }
                }
                // 4. Если есть store_id и user.stores, ищем по ним
                if (!store_name && store_id && this._user && this._user.stores && Array.isArray(this._user.stores)) {
                    const storeObj = this._user.stores.find(s => String(s.id) === String(store_id));
                    if (storeObj && typeof storeObj.name === 'string') {
                        store_name = storeObj.name;
                    }
                }
                // 5. Если не найдено — ставим '-'
                if (!store_name) store_name = '-';

                // Для игрушек: если size это объект варианта, не кладём его в size, только в variant
                let sizeValue = size;
                if (product.type === 'toy' && size && typeof size === 'object' && size.name) {
                    // Для игрушек size должен быть null или строкой, не объектом
                    sizeValue = null;
                } else if (size && typeof size === 'object' && (size.size || size.value)) {
                    // Для обуви/одежды берём строковое значение размера
                    sizeValue = size.size || size.value || null;
                } else if (size && typeof size !== 'string' && typeof size !== 'number' && size !== null) {
                    // Если size это объект без нужных полей, не кладём его
                    sizeValue = null;
                }

                const newItem = {
                    id: product.id,
                    name: product.name,
                    price: product.price,
                    img: product.image_url,
                    size: sizeValue,
                    quantity: quantity,
                    maxQuantity: maxQuantity,
                    store_id: store_id,
                    store_name: store_name,
                    variant: typeof variant === 'string' ? null : (variant && typeof variant === 'object' && Object.keys(variant).length > 0 ? variant : null),
                    discount: product.discount || 0,
                    sizeId: variant?.sizeId || variant?.id || null,
                    sizes: product.sizes || product.variants || [],
                    selectedSize: variant?.size || variant?.value || null
                };

                this._basket.push(newItem);
            }

            // Сохраняем корзину в localStorage
            this._saveBasket();
            
            return true;
        } catch (error) {
            console.error('Error adding to basket:', error);
            throw new Error('Ошибка при добавлении товара в корзину');
        }
    }

    updateBasketItem(index, quantity) {
        if (index >= 0 && index < this._basket.length) {
            const item = this._basket[index];
            const maxQty = item.maxQuantity ?? 999;
            this._basket[index].quantity = Math.min(Math.max(1, quantity), maxQty);
            this._saveBasket();
        }
    }

    removeFromBasket(index) {
        if (index >= 0 && index < this._basket.length) {
            this._basket.splice(index, 1);
            this._saveBasket();
        }
    }

    clearBasket() {
        this._basket = [];
        this._saveBasket();
    }

    _saveBasket() {
        const key = this._getBasketKey();
        localStorage.setItem(key, JSON.stringify(this._basket));
    }

    logout() {
        this._isAuth = false;
        this._user = {};
        this._basket = [];
        this._lastRefreshTime = 0;
        localStorage.removeItem('token');
        // Очищаем только корзину текущего пользователя
        localStorage.removeItem(this._getBasketKey());
    }
}